import React, { Suspense, useState } from 'react';
import { Dialog, useMediaQuery, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { popModal } from '../../store/slices/modalSlice';
import { selectModals } from '../../store/selectors';
import { MODAL_REGISTRY } from './modalRegistry';
import SlideUpTransition from '../SlideUpTransition';
import { ModalLoadingState } from './ModalLoadingState';

const GlobalModalManager: React.FC = () => {
  const modals = useAppSelector(selectModals);
  const dispatch = useAppDispatch();
  const [closingModalId, setClosingModalId] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const closeTop = () => {
    if (modals.length > 0) {
      const topModal = modals[modals.length - 1];
      setClosingModalId(topModal.id);
    }
  };

  const handleExited = () => {
    dispatch(popModal());
    setClosingModalId(null);
  };

  return (
    <>
      {modals.map((modal, idx) => {
        const {
          id,
          modalType,
          modalProps: {
            maxWidth = 'sm',
            fullHeight = false,
            fullWidth = true,
            fullScreen = false,
            fullScreenOnMobile = false,
            sx = {},
            ...modalProps
          },
        } = modal;
        const resolvedFullScreen = fullScreen || (fullScreenOnMobile && isMobile);
        const isTop = idx === modals.length - 1;
        const isFirst = idx === 0; // Only the very first gets animation
        const Component = MODAL_REGISTRY[modalType];

        if (!Component) {
          console.warn(`Modal component not found for type: ${modalType}`);
          return null;
        }

        const isClosing = closingModalId === id;
        const isOpen = !isClosing;

        return (
          <Dialog
            key={id}
            open={isOpen}
            onClose={closeTop}
            hideBackdrop={!isTop}
            slotProps={{paper: {
              sx: {
                ...(fullHeight
                  ? {
                      height: 'calc(100vh - 64px)',
                      maxHeight: 'calc(100vh - 64px)',
                      margin: '32px',
                    }
                  : {}),
                ...sx,
              },
            },
              backdrop: { invisible: !isTop },
              transition: {
                onExited: isClosing ? handleExited : undefined,
              },
            }}
            fullWidth={fullWidth}
            fullScreen={resolvedFullScreen}
            transitionDuration={isFirst || resolvedFullScreen ? undefined : 0} maxWidth={maxWidth} slots={{ transition: SlideUpTransition }}
          >
            <Suspense fallback={<ModalLoadingState modalType={modalType} />}>
              <Component modalProps={modalProps} closeModal={closeTop} />
            </Suspense>
          </Dialog>
        );
      })}
    </>
  );
};

export default GlobalModalManager;
